import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  ShieldCheck,
  ShieldOff,
  CreditCard,
  Tag,
  Save,
  Plus,
  Pencil,
  Trash2,
  X,
  ChevronLeft,
  AlertCircle,
  CheckCircle2,
  History,
  ChevronDown,
  ChevronUp,
  Eye,
  MessageSquare,
  Award,
  Search,
} from 'lucide-react';
import { adminCustomers, adminOrders, adminCommissions, adminCompanies, adminQbo } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import AccountNotesPanel from './crm/AccountNotesPanel';
import FollowUpsPanel from './crm/FollowUpsPanel';
import LocationsContactsPanel from './crm/LocationsContactsPanel';

// ── Status badge helper ────────────────────────────────────────────────────────
const statusBadge = (s) => {
  const map = {
    pending: 'bg-warning text-dark',
    confirmed: 'bg-info',
    shipped: 'bg-primary',
    delivered: 'bg-success',
    invoiced: 'bg-secondary',
    paid: 'bg-dark',
  };
  return (
    <span className={`badge ${map[s] || 'bg-secondary'}`}>
      {s}
    </span>
  );
};

const paymentStatusBadge = (s) => {
  const map = {
    unpaid: 'bg-warning text-dark',
    pending: 'bg-warning text-dark',
    paid: 'bg-success',
    failed: 'bg-danger',
    refunded: 'bg-secondary',
  };
  return (
    <span className={`badge ${map[s] || 'bg-secondary'}`}>
      {s || '—'}
    </span>
  );
};

// ── Credit progress bar helper ─────────────────────────────────────────────────
function CreditBar({ creditCap, outstandingBalance, availableCredit }) {
  if (!creditCap || creditCap === 0) {
    return <span className="text-muted small">No credit cap set</span>;
  }
  const used = outstandingBalance || 0;
  const pctUsed = Math.min(100, Math.round((used / creditCap) * 100));
  const pctAvailable = 100 - pctUsed;

  let barClass = 'bg-success';
  if (pctAvailable <= 25) barClass = 'bg-danger';
  else if (pctAvailable <= 50) barClass = 'bg-warning';

  const fmt = (v) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

  return (
    <div>
      <div className="d-flex justify-content-between small mb-1">
        <span>Available: <strong className={pctAvailable <= 25 ? 'text-danger' : pctAvailable <= 50 ? 'text-warning' : 'text-success'}>{fmt(availableCredit)}</strong></span>
        <span>Used: {fmt(used)} / {fmt(creditCap)}</span>
      </div>
      <div className="progress" style={{ height: '10px' }}>
        <div
          className={`progress-bar ${barClass}`}
          role="progressbar"
          style={{ width: `${pctAvailable}%` }}
          aria-valuenow={pctAvailable}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // ── Core state ───────────────────────────────────────────────────────────────
  const [loading, setLoading]       = useState(true);
  const [customer, setCustomer]     = useState(null);
  const [reps, setReps]             = useState([]);
  const [orders, setOrders]         = useState([]);
  const [overrides, setOverrides]   = useState([]);
  const [activeTab, setActiveTab]   = useState('account');

  // ── Alert state ──────────────────────────────────────────────────────────────
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const successTimer = useRef(null);

  // ── Overview form state ──────────────────────────────────────────────────────
  const [form, setForm] = useState({
    // Address
    addressLine: '',
    city: '',
    state: '',
    zip: '',
    // License
    abcLicenseNumber: '',
    resaleNumber: '',
    // Payment
    paymentType: 'invoice',
    netDays: 30,
    creditCap: '',
    // Rep
    assignedRepID: '',
    // Approval
    isApproved: false,
    // Bonus exemption
    bonusExempt: false,
  });
  const [saving,             setSaving]             = useState(false);
  const [bonusExemptSaving,  setBonusExemptSaving]  = useState(false);

  // ── Pricing tab state ────────────────────────────────────────────────────────
  const [showAddOverrideModal, setShowAddOverrideModal] = useState(false);
  const [overrideForm, setOverrideForm] = useState({ productID: '', customPrice: '', reason: '' });
  const [overrideFormError, setOverrideFormError] = useState('');
  const [overrideSaving, setOverrideSaving]   = useState(false);
  const [editingOverride, setEditingOverride] = useState(null);
  const [deletingOverrideId, setDeletingOverrideId] = useState(null);

  // Price level
  const [priceLevel, setPriceLevel]             = useState('standard');
  const [priceLevelDiscount, setPriceLevelDiscount] = useState('0');
  const [priceLevelSaving, setPriceLevelSaving] = useState(false);

  // Pricing history accordion
  const [historyOpen, setHistoryOpen]       = useState(false);
  const [pricingHistory, setPricingHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ── Notes tab state ──────────────────────────────────────────────────────────
  const [customerNotes, setCustomerNotes] = useState('');
  const [tagInput, setTagInput]           = useState('');
  const [tags, setTags]                   = useState([]);
  const [notesSaving, setNotesSaving]     = useState(false);

  // ── Incentive bonus state ─────────────────────────────────────────────────────
  const [incentiveBonuses,      setIncentiveBonuses]      = useState([]);
  const [incentiveBonusLoading, setIncentiveBonusLoading] = useState(false);

  // ── Impersonation state ──────────────────────────────────────────────────────
  const [impersonateModal,   setImpersonateModal]   = useState(false);
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  // ── QBO Customer ID state ─────────────────────────────────────────────────
  const [qboCustomerId,       setQboCustomerId]       = useState('');
  const [qboCustomerIdSaved,  setQboCustomerIdSaved]  = useState(''); // last-persisted value
  const [qboCustomerIdSaving, setQboCustomerIdSaving] = useState(false);

  // ── QBO auto-create state ─────────────────────────────────────────────────
  const [qboAutocreate,       setQboAutocreate]       = useState(false);
  const [qboAutocreateSaving, setQboAutocreateSaving] = useState(false);

  // ── QBO customer search state ─────────────────────────────────────────────
  const [qboSearchLoading, setQboSearchLoading] = useState(false);
  const [qboSearchResults, setQboSearchResults] = useState(null); // null = not yet searched
  const [qboSearchError,   setQboSearchError]   = useState('');

  // ── Edit contact modal state ──────────────────────────────────────────────
  const [contactModal,   setContactModal]   = useState(false);
  const [contactForm,    setContactForm]    = useState({ firstName: '', lastName: '', phone: '', email: '' });
  const [contactSaving,  setContactSaving]  = useState(false);
  const [contactError,   setContactError]   = useState('');

  // ── Messages tab state ────────────────────────────────────────────────────
  const [messages,        setMessages]        = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesLoaded,  setMessagesLoaded]  = useState(false);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const showSuccess = useCallback((msg) => {
    setSuccess(msg);
    if (successTimer.current) clearTimeout(successTimer.current);
    successTimer.current = setTimeout(() => setSuccess(''), 4000);
  }, []);

  const showError = useCallback((msg) => {
    setError(msg);
  }, []);

  // ── Initial load ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [custRes, repsRes, ordersRes, overridesRes] = await Promise.all([
          adminCustomers.get(id),
          adminCustomers.reps(),
          adminOrders.list(),
          adminCustomers.getOverrides(id),
        ]);

        if (cancelled) return;

        const c = custRes.data.data;
        setCustomer(c);

        // Shipping address
        const shipping = (c.addresses || []).find((a) => a.addressType === 'shipping') || {};
        setForm({
          addressLine:     shipping.addressLine || '',
          city:            shipping.city        || '',
          state:           shipping.state       || '',
          zip:             shipping.zip         || '',
          abcLicenseNumber: c.abcLicenseNumber  || '',
          resaleNumber:    c.resaleNumber       || '',
          paymentType:     c.paymentType        || 'invoice',
          netDays:         c.netDays            ?? 30,
          creditCap:       c.creditCap          ?? '',
          assignedRepID:   c.assignedRepID      || '',
          isApproved:      !!c.isApproved,
          bonusExempt:     !!c.bonusExempt,
        });

        setCustomerNotes(c.customerNotes || '');
        setTags(
          c.customerTags
            ? c.customerTags.split(',').map((t) => t.trim()).filter(Boolean)
            : []
        );
        setPriceLevel(c.priceLevel || 'standard');
        setPriceLevelDiscount(c.priceLevelDiscount != null ? String(c.priceLevelDiscount) : '0');
        setQboCustomerId(c.qbo_customer_id || '');
        setQboCustomerIdSaved(c.qbo_customer_id || '');
        setQboAutocreate(!!c.qbo_autocreate);

        setReps(repsRes.data.data || []);

        // Filter orders client-side by customerID
        const allOrders = ordersRes.data.data || [];
        setOrders(allOrders.filter((o) => String(o.customerID) === String(c.customerID)));

        setOverrides(overridesRes.data.data || []);
      } catch (err) {
        if (!cancelled) showError(err.response?.data?.message || 'Failed to load customer data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [id, showError]);

  // ── Load incentive bonuses for this account ───────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setIncentiveBonusLoading(true);
    adminCommissions.incentivePayouts({ customerID: id })
      .then(res => { if (!cancelled) setIncentiveBonuses(res.data.data || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setIncentiveBonusLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  // ── Cleanup timer on unmount ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (successTimer.current) clearTimeout(successTimer.current);
    };
  }, []);

  // ── Save overview ────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await adminCustomers.update(id, {
        addressLine:      form.addressLine,
        city:             form.city,
        state:            form.state,
        zip:              form.zip,
        abcLicenseNumber: form.abcLicenseNumber,
        resaleNumber:     form.resaleNumber,
        paymentType:      form.paymentType,
        netDays:          Number(form.netDays),
        creditCap:        form.creditCap !== '' ? Number(form.creditCap) : null,
        assignedRepID:    form.assignedRepID || null,
        isApproved:       form.isApproved,
      });
      // Refresh customer data to get updated balances
      const res = await adminCustomers.get(id);
      setCustomer(res.data.data);
      showSuccess('Customer saved successfully.');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to save customer.');
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle approval ──────────────────────────────────────────────────────────
  const handleToggleApproval = () => {
    setForm((prev) => ({ ...prev, isApproved: !prev.isApproved }));
  };

  // ── Save QBO Customer ID ──────────────────────────────────────────────────────
  const handleSaveQboCustomerId = async () => {
    if (!customer?.companyID) return;
    setQboCustomerIdSaving(true);
    setError('');
    try {
      await adminCompanies.updateQboCustomerId(customer.companyID, qboCustomerId.trim());
      setQboCustomerIdSaved(qboCustomerId.trim());
      showSuccess('QBO Customer ID saved');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to save QBO Customer ID');
    } finally {
      setQboCustomerIdSaving(false);
    }
  };

  // ── Toggle QBO auto-create ────────────────────────────────────────────────────
  const handleToggleQboAutocreate = async () => {
    if (!customer?.companyID) return;
    const newVal = !qboAutocreate;
    setQboAutocreate(newVal);
    setQboAutocreateSaving(true);
    try {
      await adminCompanies.updateQboAutocreate(customer.companyID, newVal);
      showSuccess(newVal ? 'Auto-create enabled' : 'Auto-create disabled');
    } catch (err) {
      setQboAutocreate(!newVal); // revert
      showError(err.response?.data?.message || 'Failed to update auto-create setting');
    } finally {
      setQboAutocreateSaving(false);
    }
  };

  // ── Search QuickBooks for existing customer ───────────────────────────────────
  const handleSearchQbo = async () => {
    if (!customer) return;
    setQboSearchLoading(true);
    setQboSearchResults(null);
    setQboSearchError('');
    try {
      const res = await adminQbo.searchCustomer(customer.companyName, customer.email);
      setQboSearchResults(res.data.results || []);
    } catch (err) {
      setQboSearchError(err.response?.data?.message || 'Search failed. Check QBO connection.');
      setQboSearchResults([]);
    } finally {
      setQboSearchLoading(false);
    }
  };

  // ── Use a QBO search result as the customer ID ───────────────────────────────
  const handleUseQboSearchResult = async (qboId) => {
    if (!customer?.companyID) return;
    setQboCustomerIdSaving(true);
    try {
      await adminCompanies.updateQboCustomerId(customer.companyID, qboId);
      setQboCustomerId(qboId);
      setQboCustomerIdSaved(qboId);
      setQboSearchResults(null);
      showSuccess('QBO Customer ID saved');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to save QBO Customer ID');
    } finally {
      setQboCustomerIdSaving(false);
    }
  };

  // ── Toggle bonus exempt ───────────────────────────────────────────────────────
  const handleToggleBonusExempt = async () => {
    const newVal = !form.bonusExempt;
    setForm((prev) => ({ ...prev, bonusExempt: newVal }));
    setBonusExemptSaving(true);
    try {
      await adminCustomers.update(id, { bonusExempt: newVal });
      showSuccess('Bonus exemption updated.');
    } catch (err) {
      setForm((prev) => ({ ...prev, bonusExempt: !newVal }));
      showError(err.response?.data?.message || 'Failed to update bonus exemption.');
    } finally {
      setBonusExemptSaving(false);
    }
  };

  // ── Save notes (autosave on blur) ────────────────────────────────────────────
  const handleNotesBlur = async () => {
    setNotesSaving(true);
    try {
      await adminCustomers.update(id, { customerNotes });
      showSuccess('Notes saved.');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to save notes.');
    } finally {
      setNotesSaving(false);
    }
  };

  // ── Edit contact person ───────────────────────────────────────────────────────
  const openContactModal = () => {
    setContactForm({
      firstName: customer.firstName || '',
      lastName:  customer.lastName  || '',
      phone:     customer.phone     || '',
      email:     customer.email     || '',
    });
    setContactError('');
    setContactModal(true);
  };

  const handleSaveContact = async () => {
    setContactError('');
    setContactSaving(true);
    try {
      await adminCustomers.updateContact(id, contactForm);
      // Patch customer state so the card reflects the change immediately
      setCustomer(c => ({
        ...c,
        firstName: contactForm.firstName,
        lastName:  contactForm.lastName,
        phone:     contactForm.phone,
        email:     contactForm.email,
      }));
      setContactModal(false);
      showSuccess('Contact information updated.');
    } catch (err) {
      setContactError(err.response?.data?.message || 'Failed to update contact.');
    } finally {
      setContactSaving(false);
    }
  };

  // ── Impersonation ────────────────────────────────────────────────────────────
  const handleImpersonate = async () => {
    setImpersonateLoading(true);
    try {
      const res = await adminCustomers.impersonate(id);
      const { token, customerName, expiresIn } = res.data;
      // Save current admin token so it can be restored later
      localStorage.setItem('wholesaleAdminToken', localStorage.getItem('wholesaleToken'));
      // Switch to impersonation token
      localStorage.setItem('wholesaleToken', token);
      // Store impersonation metadata (expiresAt for accurate countdown on reload)
      localStorage.setItem('isImpersonating', JSON.stringify({
        customerName,
        expiresIn,
        expiresAt: Date.now() + expiresIn * 1000,
      }));
      // Hard redirect so AuthContext reinitialises with the customer token
      window.location.href = '/wholesale/dashboard';
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to start customer preview.');
      setImpersonateModal(false);
      setImpersonateLoading(false);
    }
  };

  // ── Tags helpers ─────────────────────────────────────────────────────────────
  const saveTags = async (newTags) => {
    try {
      await adminCustomers.update(id, { customerTags: newTags.join(',') });
      showSuccess('Tags saved.');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to save tags.');
    }
  };

  const addTag = (raw) => {
    const parts = raw
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    const newTags = [...new Set([...tags, ...parts])];
    setTags(newTags);
    setTagInput('');
    saveTags(newTags);
  };

  const removeTag = (tag) => {
    const newTags = tags.filter((t) => t !== tag);
    setTags(newTags);
    saveTags(newTags);
  };

  // ── Save price level ─────────────────────────────────────────────────────────
  const handleSavePriceLevel = async () => {
    setPriceLevelSaving(true);
    try {
      await adminCustomers.updatePriceLevel(id, {
        priceLevel,
        priceLevelDiscount: parseFloat(priceLevelDiscount) || 0,
      });
      showSuccess('Price level saved.');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to save price level.');
    } finally {
      setPriceLevelSaving(false);
    }
  };

  // ── Load pricing history (lazy — only when accordion opened) ─────────────────
  const handleToggleHistory = async () => {
    const opening = !historyOpen;
    setHistoryOpen(opening);
    if (opening && pricingHistory.length === 0) {
      setHistoryLoading(true);
      try {
        const res = await adminCustomers.getPricingHistory(id);
        setPricingHistory(res.data.data || []);
      } catch {
        // silently fail — history accordion will show empty state
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (tagInput.trim()) addTag(tagInput);
    }
  };

  const handleTagBlur = () => {
    if (tagInput.trim()) addTag(tagInput);
  };

  // ── Price override: add ──────────────────────────────────────────────────────
  const handleAddOverride = async () => {
    setOverrideFormError('');
    if (!overrideForm.productID.trim()) {
      setOverrideFormError('Product ID is required.');
      return;
    }
    if (!overrideForm.customPrice || isNaN(Number(overrideForm.customPrice))) {
      setOverrideFormError('A valid price is required.');
      return;
    }
    setOverrideSaving(true);
    try {
      await adminCustomers.createOverride(id, {
        productID:   overrideForm.productID.trim(),
        customPrice: Number(overrideForm.customPrice),
        reason:      overrideForm.reason.trim() || null,
      });
      const res = await adminCustomers.getOverrides(id);
      setOverrides(res.data.data || []);
      setOverrideForm({ productID: '', customPrice: '', reason: '' });
      setShowAddOverrideModal(false);
      showSuccess('Override added.');
    } catch (err) {
      setOverrideFormError(err.response?.data?.message || 'Failed to add override.');
    } finally {
      setOverrideSaving(false);
    }
  };

  // ── Price override: start edit ───────────────────────────────────────────────
  const startEditOverride = (ov) => {
    setEditingOverride({
      overrideID:  ov.overrideID,
      productID:   String(ov.productID),
      customPrice: String(ov.customPrice),
      reason:      ov.reason || '',
    });
  };

  // ── Price override: save edit ────────────────────────────────────────────────
  const saveEditOverride = async () => {
    if (!editingOverride.customPrice || isNaN(Number(editingOverride.customPrice))) {
      showError('A valid price is required.');
      return;
    }
    try {
      await adminCustomers.updateOverride(id, editingOverride.overrideID, {
        customPrice: Number(editingOverride.customPrice),
        reason:      editingOverride.reason || null,
      });
      const res = await adminCustomers.getOverrides(id);
      setOverrides(res.data.data || []);
      setEditingOverride(null);
      showSuccess('Override updated.');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to update override.');
    }
  };

  // ── Price override: delete ───────────────────────────────────────────────────
  const handleDeleteOverride = async (overrideId) => {
    if (!window.confirm('Delete this price override?')) return;
    setDeletingOverrideId(overrideId);
    try {
      await adminCustomers.deleteOverride(id, overrideId);
      setOverrides((prev) => prev.filter((o) => o.overrideID !== overrideId));
      showSuccess('Override deleted.');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to delete override.');
    } finally {
      setDeletingOverrideId(null);
    }
  };

  // ── Load messages (lazy — only when messages tab is first opened) ────────────
  const loadMessages = async () => {
    setMessagesLoading(true);
    try {
      const res = await adminCustomers.getMessages(id);
      setMessages(res.data.data || []);
      setMessagesLoaded(true);
    } catch {
      setMessagesLoaded(true);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Messages are lazy-loaded; trigger on first Notes tab visit
    if (tab === 'notes' && !messagesLoaded) {
      loadMessages();
    }
  };

  // ── Currency formatter ───────────────────────────────────────────────────────
  const fmt = (v) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

  // ── Date formatter ───────────────────────────────────────────────────────────
  const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // ── Render: loading ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!customer && !loading) {
    return (
      <div className="container-fluid p-4">
        {error && (
          <div className="alert alert-danger d-flex align-items-center gap-2">
            <AlertCircle size={18} />
            {error}
          </div>
        )}
        <p className="text-muted">Customer not found.</p>
      </div>
    );
  }

  const shippingAddr = (customer.addresses || []).find((a) => a.addressType === 'shipping') || {};

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid p-4">
      {/* ── Back link ── */}
      <button
        className="btn btn-link text-decoration-none p-0 mb-3 text-secondary"
        onClick={() => navigate('/admin/customers')}
      >
        <ChevronLeft size={16} className="me-1" />
        Back to Customers
      </button>

      {/* ── Page header ── */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="mb-0 fw-bold">{customer.companyName}</h4>
          <small className="text-muted">
            {customer.firstName} {customer.lastName} &bull; {customer.email}
          </small>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <span className={`badge fs-6 ${customer.isApproved ? 'bg-success' : 'bg-warning text-dark'}`}>
            {customer.isApproved ? 'Approved' : 'Pending'}
          </span>
          <span className={`badge fs-6 ${customer.isActive ? 'bg-success' : 'bg-danger'}`}>
            {customer.isActive ? 'Active' : 'Inactive'}
          </span>
          {customer.bonusExempt ? (
            <span className="badge fs-6 bg-warning text-dark">Bonus Exempt</span>
          ) : null}
        </div>
      </div>

      {/* ── Global alerts ── */}
      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2 mb-3" role="alert">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button
            type="button"
            className="btn-close ms-auto"
            onClick={() => setError('')}
            aria-label="Close"
          />
        </div>
      )}
      {success && (
        <div className="alert alert-success d-flex align-items-center gap-2 mb-3" role="alert">
          <CheckCircle2 size={18} />
          <span>{success}</span>
        </div>
      )}

      {/* ── Tabs ── */}
      <ul className="nav nav-tabs mb-4">
        {[
          { key: 'account',  label: 'Account' },
          { key: 'contacts', label: 'Contacts' },
          { key: 'orders',   label: 'Orders' },
          { key: 'pricing',  label: 'Pricing' },
          { key: 'notes',    label: 'Notes' },
          // QuickBooks tab: only added to the DOM for admin role
          ...(user?.role === 'admin'
            ? [{ key: 'quickbooks', label: 'QuickBooks' }]
            : []),
        ].map(({ key, label }) => (
          <li className="nav-item" key={key}>
            <button
              className={`nav-link ${activeTab === key ? 'active' : ''}`}
              onClick={() => handleTabChange(key)}
            >
              {label}
            </button>
          </li>
        ))}
      </ul>

      <div className="tab-content">
        {/* ================================================================
            ACCOUNT TAB
        ================================================================ */}
        {activeTab === 'account' && (
          <div className="tab-pane show active">
            <div className="row g-4">
              {/* ── Left column ── */}
              <div className="col-md-6">
                {/* Account Status card */}
                <div className="card mb-4">
                  <div className="card-header bg-light d-flex align-items-center gap-2">
                    <ShieldCheck size={16} />
                    <strong>Account Status</strong>
                  </div>
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div>
                        <div className="fw-semibold">Approval Status</div>
                        <small className="text-muted">
                          {form.isApproved
                            ? 'Customer is approved to place orders.'
                            : 'Customer is pending approval.'}
                        </small>
                      </div>
                      <button
                        className={`btn btn-sm ${form.isApproved ? 'btn-outline-danger' : 'btn-success'}`}
                        onClick={handleToggleApproval}
                      >
                        {form.isApproved ? (
                          <><ShieldOff size={14} className="me-1" />Suspend</>
                        ) : (
                          <><ShieldCheck size={14} className="me-1" />Approve</>
                        )}
                      </button>
                    </div>
                    <div>
                      <span className="text-muted small">Portal Active: </span>
                      <span className={`badge ${customer.isActive ? 'bg-success' : 'bg-danger'}`}>
                        {customer.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="mt-3 pt-3 border-top">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="bonusExemptSwitch"
                          checked={form.bonusExempt}
                          onChange={handleToggleBonusExempt}
                          disabled={bonusExemptSaving}
                        />
                        <label className="form-check-label" htmlFor="bonusExemptSwitch">
                          Exempt from new-account bonus
                          {bonusExemptSaving && (
                            <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />
                          )}
                        </label>
                      </div>
                      <small className="text-muted d-block mt-1">
                        Enable for imported legacy accounts to prevent incorrect bonus payouts.
                      </small>
                    </div>
                    {user?.role === 'admin' && (
                      <div className="mt-3 pt-3 border-top">
                        <button
                          className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                          onClick={() => setImpersonateModal(true)}
                        >
                          <Eye size={14} /> View as Customer
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Shipping address card */}
                <div className="card mb-4">
                  <div className="card-header bg-light d-flex align-items-center gap-2">
                    <MapPin size={16} />
                    <strong>Shipping Address</strong>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="form-label">Address Line</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.addressLine}
                        onChange={(e) => setForm((p) => ({ ...p, addressLine: e.target.value }))}
                        placeholder="123 Main St"
                      />
                    </div>
                    <div className="row g-2">
                      <div className="col-5">
                        <label className="form-label">City</label>
                        <input
                          type="text"
                          className="form-control"
                          value={form.city}
                          onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                        />
                      </div>
                      <div className="col-3">
                        <label className="form-label">State</label>
                        <input
                          type="text"
                          className="form-control"
                          value={form.state}
                          onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))}
                          maxLength={2}
                          placeholder="CA"
                        />
                      </div>
                      <div className="col-4">
                        <label className="form-label">ZIP</label>
                        <input
                          type="text"
                          className="form-control"
                          value={form.zip}
                          onChange={(e) => setForm((p) => ({ ...p, zip: e.target.value }))}
                          placeholder="90210"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* License card */}
                <div className="card mb-4">
                  <div className="card-header bg-light d-flex align-items-center gap-2">
                    <FileText size={16} />
                    <strong>License &amp; Resale</strong>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="form-label">ABC License Number</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.abcLicenseNumber}
                        onChange={(e) => setForm((p) => ({ ...p, abcLicenseNumber: e.target.value }))}
                      />
                    </div>
                    <div className="mb-0">
                      <label className="form-label">Resale Number</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.resaleNumber}
                        onChange={(e) => setForm((p) => ({ ...p, resaleNumber: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Right column ── */}
              <div className="col-md-6">
                {/* Payment Settings card */}
                <div className="card mb-4">
                  <div className="card-header bg-light d-flex align-items-center gap-2">
                    <CreditCard size={16} />
                    <strong>Payment Settings</strong>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="form-label">Payment Type</label>
                      <select
                        className="form-select"
                        value={form.paymentType}
                        onChange={(e) => setForm((p) => ({ ...p, paymentType: e.target.value }))}
                      >
                        <option value="invoice">Invoice (Net Terms)</option>
                        <option value="credit_card">Credit Card</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                    {(form.paymentType === 'invoice' || form.paymentType === 'both') && (
                      <>
                        <div className="mb-3">
                          <label className="form-label">Net Days</label>
                          <input
                            type="number"
                            className="form-control"
                            value={form.netDays}
                            min={0}
                            onChange={(e) => setForm((p) => ({ ...p, netDays: e.target.value }))}
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Credit Cap ($)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={form.creditCap}
                            min={0}
                            step="0.01"
                            placeholder="0.00"
                            onChange={(e) => setForm((p) => ({ ...p, creditCap: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="form-label d-block">Available Credit</label>
                          <CreditBar
                            creditCap={customer.creditCap}
                            outstandingBalance={customer.outstandingBalance}
                            availableCredit={customer.availableCredit}
                          />
                          <small className="text-muted d-block mt-1">
                            Outstanding: {fmt(customer.outstandingBalance)} &nbsp;|&nbsp; Available: {fmt(customer.availableCredit)}
                          </small>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Assigned rep card */}
                <div className="card mb-4">
                  <div className="card-header bg-light d-flex align-items-center gap-2">
                    <User size={16} />
                    <strong>Assigned Sales Rep</strong>
                  </div>
                  <div className="card-body">
                    <select
                      className="form-select"
                      value={form.assignedRepID}
                      onChange={(e) => setForm((p) => ({ ...p, assignedRepID: e.target.value }))}
                    >
                      <option value="">— Unassigned —</option>
                      {reps.map((rep) => (
                        <option key={rep.id} value={rep.id}>
                          {rep.firstName} {rep.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Save button */}
                <div className="d-flex justify-content-end">
                  <button
                    className="btn btn-primary px-4"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <Save size={16} className="me-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* ── Incentive Bonus History (full width) ── */}
            <div className="card mt-4">
              <div className="card-header bg-light d-flex align-items-center gap-2">
                <Award size={16} />
                <strong>Incentive Bonus History</strong>
              </div>
              <div className="card-body">
                {incentiveBonusLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-secondary" role="status">
                      <span className="visually-hidden">Loading…</span>
                    </div>
                  </div>
                ) : incentiveBonuses.length === 0 ? (
                  <p className="text-muted small mb-0">No bonuses recorded for this account.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-striped align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Bonus Type</th>
                          <th>Amount</th>
                          <th>Rep</th>
                          <th>Earned</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {incentiveBonuses.map(b => (
                          <tr key={b.payoutID}>
                            <td>
                              <span className="badge bg-info text-dark text-capitalize">
                                {b.bonusType === 'new_account_3case' ? 'New Account — 3+ Cases' : 'New Account — 1+ Cases'}
                              </span>
                            </td>
                            <td>{fmt(b.amount)}</td>
                            <td className="small text-muted">{b.repName || '—'}</td>
                            <td className="small">{fmtDate(b.earnedDate)}</td>
                            <td>
                              <span className={`badge ${b.status === 'paid' ? 'bg-dark' : 'bg-warning text-dark'}`}>
                                {b.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ================================================================
            CONTACTS TAB
        ================================================================ */}
        {activeTab === 'contacts' && (
          <div className="tab-pane show active">
            {/* Primary contact card */}
            <div className="card mb-4">
              <div className="card-header bg-light d-flex align-items-center justify-content-between">
                <span className="d-flex align-items-center gap-2">
                  <Building2 size={16} />
                  <strong>Primary Contact</strong>
                </span>
                <button
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                  onClick={openContactModal}
                >
                  <Pencil size={13} /> Edit Contact
                </button>
              </div>
              <div className="card-body">
                <dl className="row mb-0">
                  <dt className="col-sm-3">Company</dt>
                  <dd className="col-sm-9">{customer.companyName || '—'}</dd>
                  <dt className="col-sm-3">Name</dt>
                  <dd className="col-sm-9">{customer.firstName} {customer.lastName}</dd>
                  <dt className="col-sm-3">Email</dt>
                  <dd className="col-sm-9">
                    <a href={`mailto:${customer.email}`}>{customer.email}</a>
                    <small className="text-muted ms-2">(login email)</small>
                  </dd>
                  <dt className="col-sm-3">Phone</dt>
                  <dd className="col-sm-9">{customer.phone || '—'}</dd>
                </dl>
              </div>
            </div>

            {/* Multi-location contacts */}
            <LocationsContactsPanel
              customerID={id}
              loginEmail={customer?.email}
            />
          </div>
        )}

        {/* ================================================================
            ORDERS TAB
        ================================================================ */}
        {activeTab === 'orders' && (
          <div className="tab-pane show active">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="mb-0">Orders</h5>
              <button
                className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                onClick={() => navigate(`/admin/orders/create?customer=${id}`)}
              >
                <Plus size={15} />
                Create Order
              </button>
            </div>
            {orders.length === 0 ? (
              <div className="text-center text-muted py-5">
                <FileText size={40} className="mb-2 opacity-50" />
                <p className="mb-0">No orders found for this customer.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover table-striped align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Order #</th>
                      <th>Date</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Status</th>
                      <th>Payment Status</th>
                      <th>Due Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr
                        key={o.orderID}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/admin/orders/${o.orderID}`)}
                      >
                        <td className="fw-semibold">#{o.orderID}</td>
                        <td>{fmtDate(o.orderDate)}</td>
                        <td>{o.itemCount ?? (o.items?.length ?? '—')}</td>
                        <td>{fmt(o.totalAmount)}</td>
                        <td>{statusBadge(o.orderStatus)}</td>
                        <td>{paymentStatusBadge(o.paymentStatus)}</td>
                        <td>{fmtDate(o.invoiceDueDate || o.paymentDueDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ================================================================
            PRICING TAB
        ================================================================ */}
        {activeTab === 'pricing' && (
          <div className="tab-pane show active">

            {/* ── Price Level Card ── */}
            <div className="card mb-4">
              <div className="card-header bg-light fw-semibold">Price Level</div>
              <div className="card-body">
                <div className="row g-3 align-items-end">
                  <div className="col-sm-4">
                    <label className="form-label">Price Level</label>
                    <select
                      className="form-select"
                      value={priceLevel}
                      onChange={(e) => setPriceLevel(e.target.value)}
                    >
                      <option value="standard">Standard (base price)</option>
                      <option value="silver">Silver</option>
                      <option value="gold">Gold</option>
                      <option value="platinum">Platinum</option>
                      <option value="custom">Custom (per-product overrides)</option>
                    </select>
                  </div>
                  {['silver', 'gold', 'platinum'].includes(priceLevel) && (
                    <div className="col-sm-3">
                      <label className="form-label">Discount %</label>
                      <div className="input-group">
                        <input
                          type="number"
                          className="form-control"
                          min="0"
                          max="100"
                          step="0.5"
                          value={priceLevelDiscount}
                          onChange={(e) => setPriceLevelDiscount(e.target.value)}
                        />
                        <span className="input-group-text">%</span>
                      </div>
                      <div className="form-text">
                        At {parseFloat(priceLevelDiscount) || 0}% discount, this customer pays{' '}
                        <strong>{100 - (parseFloat(priceLevelDiscount) || 0)}%</strong> of base wholesale price.
                      </div>
                    </div>
                  )}
                  <div className="col-sm-auto">
                    <button
                      className="btn btn-primary d-flex align-items-center gap-1"
                      onClick={handleSavePriceLevel}
                      disabled={priceLevelSaving}
                    >
                      {priceLevelSaving
                        ? <span className="spinner-border spinner-border-sm" />
                        : <Save size={14} />}
                      Save Level
                    </button>
                  </div>
                </div>

                {/* Warning when overrides exist but level is not custom */}
                {priceLevel !== 'custom' && overrides.length > 0 && (
                  <div className="alert alert-warning d-flex align-items-start gap-2 mt-3 mb-0">
                    <AlertCircle size={16} className="mt-1 flex-shrink-0" />
                    <span>
                      This customer has <strong>{overrides.length}</strong> product override{overrides.length !== 1 ? 's' : ''}.
                      Switching to a non-custom level will not delete them, but they will be{' '}
                      <strong>ignored</strong> unless you switch back to Custom.
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Section 1: Customer Price Overrides (only shown for Custom level) */}
            {priceLevel === 'custom' && (
            <>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="mb-0">Customer Price Overrides</h5>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setOverrideForm({ productID: '', customPrice: '', reason: '' });
                  setOverrideFormError('');
                  setShowAddOverrideModal(true);
                }}
              >
                <Plus size={15} className="me-1" />
                Add Override
              </button>
            </div>

            {overrides.length === 0 ? (
              <div className="text-center text-muted py-4 border rounded mb-4">
                <Tag size={32} className="mb-2 opacity-50" />
                <p className="mb-0">No price overrides for this customer.</p>
              </div>
            ) : (
              <div className="table-responsive mb-4">
                <table className="table table-hover table-striped align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Product ID</th>
                      <th>Vintage</th>
                      <th>Base Price</th>
                      <th>Override Price</th>
                      <th>Reason</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overrides.map((ov) => {
                      const isEditing = editingOverride?.overrideID === ov.overrideID;
                      return (
                        <tr key={ov.overrideID}>
                          <td>{ov.productID}</td>
                          <td>{ov.proVintage || '—'}</td>
                          <td>{ov.wholesalePrice != null ? fmt(ov.wholesalePrice) : '—'}</td>
                          <td>
                            {isEditing ? (
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                style={{ width: '110px' }}
                                value={editingOverride.customPrice}
                                min={0}
                                step="0.01"
                                onChange={(e) =>
                                  setEditingOverride((p) => ({ ...p, customPrice: e.target.value }))
                                }
                              />
                            ) : (
                              fmt(ov.customPrice)
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={editingOverride.reason}
                                onChange={(e) =>
                                  setEditingOverride((p) => ({ ...p, reason: e.target.value }))
                                }
                                placeholder="Reason (optional)"
                              />
                            ) : (
                              ov.reason || <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <div className="d-flex gap-1">
                                <button
                                  className="btn btn-success btn-sm"
                                  onClick={saveEditOverride}
                                >
                                  <Save size={13} />
                                </button>
                                <button
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => setEditingOverride(null)}
                                >
                                  <X size={13} />
                                </button>
                              </div>
                            ) : (
                              <div className="d-flex gap-1">
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  title="Edit"
                                  onClick={() => startEditOverride(ov)}
                                >
                                  <Pencil size={13} />
                                </button>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  title="Delete"
                                  disabled={deletingOverrideId === ov.overrideID}
                                  onClick={() => handleDeleteOverride(ov.overrideID)}
                                >
                                  {deletingOverrideId === ov.overrideID ? (
                                    <span className="spinner-border spinner-border-sm" role="status" />
                                  ) : (
                                    <Trash2 size={13} />
                                  )}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Section 2: Volume pricing note */}
            <div className="alert alert-info d-flex align-items-center gap-2">
              <AlertCircle size={18} />
              <span>
                Volume pricing tiers are managed on the <strong>Products</strong> page.
              </span>
            </div>
            </>
            )}

            {/* ── Pricing History Accordion ── */}
            <div className="card mt-4">
              <div
                className="card-header bg-light d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={handleToggleHistory}
              >
                <div className="d-flex align-items-center gap-2 fw-semibold">
                  <History size={16} />
                  Pricing History
                </div>
                {historyOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
              {historyOpen && (
                <div className="card-body p-0">
                  {historyLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border spinner-border-sm text-primary" />
                    </div>
                  ) : pricingHistory.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <History size={28} className="mb-2 opacity-50" />
                      <p className="mb-0 small">No pricing changes recorded yet.</p>
                    </div>
                  ) : (
                    <div className="list-group list-group-flush">
                      {pricingHistory.map((entry) => {
                        const typeLabel = {
                          level_change:    'Price level changed',
                          override_add:    'Override added',
                          override_edit:   'Override edited',
                          override_delete: 'Override deleted',
                        }[entry.changeType] || entry.changeType;
                        const typeBadge = {
                          level_change:    'bg-primary',
                          override_add:    'bg-success',
                          override_edit:   'bg-warning text-dark',
                          override_delete: 'bg-danger',
                        }[entry.changeType] || 'bg-secondary';

                        let prev = null, next = null;
                        try { prev = entry.previousValue ? JSON.parse(entry.previousValue) : null; } catch {}
                        try { next = entry.newValue ? JSON.parse(entry.newValue) : null; } catch {}

                        return (
                          <div key={entry.historyID} className="list-group-item px-4 py-3">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <span className={`badge ${typeBadge} me-2`}>{typeLabel}</span>
                                <small className="text-muted">
                                  {new Date(entry.createdAt).toLocaleString('en-US')}
                                  {entry.changedByName && ` · ${entry.changedByName}`}
                                </small>
                              </div>
                            </div>
                            {(prev || next) && (
                              <div className="mt-2 d-flex gap-3">
                                {prev && (
                                  <div className="flex-grow-1">
                                    <small className="text-muted d-block mb-1">Before</small>
                                    <pre className="bg-light rounded p-2 mb-0 small" style={{ fontSize: 11 }}>
                                      {JSON.stringify(prev, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                {next && (
                                  <div className="flex-grow-1">
                                    <small className="text-muted d-block mb-1">After</small>
                                    <pre className="bg-light rounded p-2 mb-0 small" style={{ fontSize: 11 }}>
                                      {JSON.stringify(next, null, 2)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Add Override Modal ── */}
            {showAddOverrideModal && (
              <>
                <div
                  className="modal fade show d-block"
                  tabIndex="-1"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="addOverrideModalLabel"
                >
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title" id="addOverrideModalLabel">Add Price Override</h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowAddOverrideModal(false)}
                          aria-label="Close"
                        />
                      </div>
                      <div className="modal-body">
                        {overrideFormError && (
                          <div className="alert alert-danger py-2">{overrideFormError}</div>
                        )}
                        <div className="mb-3">
                          <label className="form-label">Product ID</label>
                          <input
                            type="text"
                            className="form-control"
                            value={overrideForm.productID}
                            onChange={(e) =>
                              setOverrideForm((p) => ({ ...p, productID: e.target.value }))
                            }
                            placeholder="Enter product ID"
                            autoFocus
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Override Price ($)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={overrideForm.customPrice}
                            min={0}
                            step="0.01"
                            placeholder="0.00"
                            onChange={(e) =>
                              setOverrideForm((p) => ({ ...p, customPrice: e.target.value }))
                            }
                          />
                        </div>
                        <div className="mb-0">
                          <label className="form-label">Reason <span className="text-muted">(optional)</span></label>
                          <input
                            type="text"
                            className="form-control"
                            value={overrideForm.reason}
                            onChange={(e) =>
                              setOverrideForm((p) => ({ ...p, reason: e.target.value }))
                            }
                            placeholder="e.g. Preferred account"
                          />
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowAddOverrideModal(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleAddOverride}
                          disabled={overrideSaving}
                        >
                          {overrideSaving ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                              Saving…
                            </>
                          ) : (
                            'Add Override'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-backdrop fade show" onClick={() => setShowAddOverrideModal(false)} />
              </>
            )}
          </div>
        )}

        {/* ================================================================
            NOTES TAB — internal notes · tags · CRM notes · follow-ups · messages
        ================================================================ */}
        {activeTab === 'notes' && (
          <div className="tab-pane show active">

            {/* ── Internal Notes + Tags ── */}
            <div className="row g-4 mb-4">
              <div className="col-12 col-lg-7">
                <div className="card h-100">
                  <div className="card-header bg-light d-flex align-items-center justify-content-between">
                    <strong>Internal Notes</strong>
                    {notesSaving && (
                      <span className="text-muted small d-flex align-items-center gap-1">
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        Saving…
                      </span>
                    )}
                  </div>
                  <div className="card-body">
                    <textarea
                      className="form-control"
                      rows={8}
                      value={customerNotes}
                      onChange={(e) => setCustomerNotes(e.target.value)}
                      onBlur={handleNotesBlur}
                      placeholder="Internal notes about this customer… (autosaved on blur)"
                    />
                    <small className="text-muted mt-1 d-block">Notes are autosaved when you click away.</small>
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-5">
                <div className="card h-100">
                  <div className="card-header bg-light">
                    <strong>Tags</strong>
                  </div>
                  <div className="card-body">
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {tags.length === 0 && (
                        <span className="text-muted small">No tags yet.</span>
                      )}
                      {tags.map((tag) => (
                        <span
                          key={tag}
                          className="badge bg-secondary d-flex align-items-center gap-1"
                          style={{ fontSize: '0.85rem' }}
                        >
                          {tag}
                          <button
                            type="button"
                            className="btn-close btn-close-white"
                            style={{ fontSize: '0.6rem' }}
                            aria-label={`Remove tag ${tag}`}
                            onClick={() => removeTag(tag)}
                          />
                        </span>
                      ))}
                    </div>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Add a tag — press Enter or comma to add"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleTagKeyDown}
                        onBlur={handleTagBlur}
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => { if (tagInput.trim()) addTag(tagInput); }}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                    <small className="text-muted mt-1 d-block">
                      Press <kbd>Enter</kbd> or <kbd>,</kbd> to add a tag. Tags are saved automatically.
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* ── CRM Account Notes ── */}
            <AccountNotesPanel
              customerID={id}
              currentUserID={user?.id}
              currentUserRole={user?.role}
            />

            {/* ── Follow-Ups ── */}
            <div className="mt-4">
              <FollowUpsPanel
                customerID={id}
                currentUserID={user?.id}
                currentUserRole={user?.role}
              />
            </div>

            {/* ── Messages from Customer ── */}
            <div className="card mt-4">
              <div className="card-header bg-light d-flex align-items-center gap-2">
                <MessageSquare size={16} />
                <strong>Messages from Customer</strong>
              </div>
              <div className="card-body p-0">
                {messagesLoading ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading…</span>
                    </div>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <MessageSquare size={36} className="mb-2 opacity-50" />
                    <p className="mb-0 small">No messages from this customer yet.</p>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {messages.map((msg) => (
                      <div
                        key={msg.messageID}
                        className={`list-group-item ${!msg.isRead ? 'list-group-item-warning' : ''}`}
                      >
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <div className="d-flex align-items-center gap-2">
                            {!msg.isRead && (
                              <span className="badge bg-warning text-dark">New</span>
                            )}
                            <strong>{msg.subject}</strong>
                          </div>
                          <small className="text-muted text-nowrap ms-3">
                            {fmtDate(msg.createdAt)}
                          </small>
                        </div>
                        <p className="mb-0 text-muted small" style={{ whiteSpace: 'pre-wrap' }}>
                          {msg.body}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>
        )}

        {/* ================================================================
            QUICKBOOKS TAB — admin only (never rendered for sales_rep)
        ================================================================ */}
        {activeTab === 'quickbooks' && user?.role === 'admin' && (
          <div className="tab-pane show active">

            {/* ── Sync status banner ── */}
            {qboCustomerIdSaved ? (
              <div className="alert alert-success d-flex align-items-center gap-2 mb-4">
                <CheckCircle2 size={18} />
                <span>Linked to QuickBooks Customer ID: <strong>{qboCustomerIdSaved}</strong></span>
              </div>
            ) : qboAutocreate ? (
              <div className="alert alert-info d-flex align-items-center gap-2 mb-4">
                <AlertCircle size={18} />
                <span>Auto-create is enabled. This customer will be created in QuickBooks on first invoice sync.</span>
              </div>
            ) : (
              <div className="alert alert-warning d-flex align-items-center gap-2 mb-4">
                <AlertCircle size={18} />
                <span>No QuickBooks customer linked. Orders may be placed on hold during invoice sync.</span>
              </div>
            )}

            {/* ── QBO Customer ID ── */}
            <div className="card mb-4">
              <div className="card-header bg-light fw-semibold">QuickBooks Customer ID</div>
              <div className="card-body">
                <div className="row g-2 align-items-end">
                  <div className="col-sm-6">
                    <label className="form-label">Customer ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={qboCustomerId}
                      onChange={(e) => setQboCustomerId(e.target.value)}
                      placeholder="e.g. 123"
                    />
                    {qboCustomerIdSaved && !qboCustomerId.trim() && (
                      <div className="form-text text-warning">Save with an empty field to remove the ID assignment.</div>
                    )}
                  </div>
                  <div className="col-sm-auto">
                    <button
                      className="btn btn-primary d-inline-flex align-items-center gap-1"
                      onClick={handleSaveQboCustomerId}
                      disabled={qboCustomerIdSaving || qboCustomerId.trim() === qboCustomerIdSaved}
                    >
                      {qboCustomerIdSaving
                        ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        : <Save size={14} />}
                      Save ID
                    </button>
                  </div>
                </div>
                <div className="form-text mt-2">
                  Links this account to a QuickBooks customer for invoice syncing.
                  Clear the field and save to remove the link.
                </div>
              </div>
            </div>

            {/* ── Auto-create toggle ── */}
            <div className="card mb-4">
              <div className="card-header bg-light fw-semibold">Auto-Create in QuickBooks</div>
              <div className="card-body">
                <div className="form-check form-switch mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="qboAutocreateSwitch"
                    checked={qboAutocreate}
                    onChange={handleToggleQboAutocreate}
                    disabled={qboAutocreateSaving || !!qboCustomerIdSaved}
                  />
                  <label className="form-check-label" htmlFor="qboAutocreateSwitch">
                    Auto-create this customer in QuickBooks on first invoice sync
                    {qboAutocreateSaving && (
                      <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />
                    )}
                  </label>
                </div>
                {qboCustomerIdSaved ? (
                  <div className="text-muted small">
                    Auto-create is disabled — a QBO Customer ID is already assigned.
                  </div>
                ) : (
                  <div className="text-muted small">
                    When enabled, the system will attempt to create this customer in QuickBooks the first
                    time an invoice is synced, instead of placing the order on hold.
                    Search for an existing customer below before enabling this option.
                  </div>
                )}
              </div>
            </div>

            {/* ── Search QuickBooks ── */}
            <div className="card">
              <div className="card-header bg-light fw-semibold">Search QuickBooks for Existing Customer</div>
              <div className="card-body">
                <p className="text-muted small mb-3">
                  Search by company name and email to find an existing QuickBooks customer record.
                  Use this to link the account before enabling auto-create.
                </p>
                <button
                  className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                  onClick={handleSearchQbo}
                  disabled={qboSearchLoading}
                >
                  {qboSearchLoading ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  ) : (
                    <Search size={15} />
                  )}
                  Search QuickBooks
                </button>

                {qboSearchError && (
                  <div className="alert alert-danger mt-3 mb-0 py-2 small">{qboSearchError}</div>
                )}

                {qboSearchResults !== null && !qboSearchError && (
                  qboSearchResults.length === 0 ? (
                    <div className="alert alert-info mt-3 mb-0 py-2 small">
                      No matching customers found in QuickBooks for &ldquo;{customer?.companyName}&rdquo;.
                    </div>
                  ) : (
                    <div className="mt-3">
                      <p className="small text-muted mb-2">
                        {qboSearchResults.length} result{qboSearchResults.length !== 1 ? 's' : ''} found.
                        Click &ldquo;Use This Customer&rdquo; to link the account.
                      </p>
                      <div className="list-group">
                        {qboSearchResults.map((r) => (
                          <div
                            key={r.qboCustomerId}
                            className="list-group-item d-flex justify-content-between align-items-center"
                          >
                            <div>
                              <div className="fw-semibold">{r.displayName}</div>
                              <small className="text-muted">
                                {r.email && <>{r.email} &middot; </>}
                                {r.primaryAddr && <>{r.primaryAddr} &middot; </>}
                                ID: {r.qboCustomerId}
                              </small>
                            </div>
                            <button
                              className="btn btn-sm btn-primary ms-3"
                              onClick={() => handleUseQboSearchResult(r.qboCustomerId)}
                              disabled={qboCustomerIdSaving}
                            >
                              {qboCustomerIdSaving ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                              ) : (
                                'Use This Customer'
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>

          </div>
        )}
      </div>

      {/* ── Edit Contact modal ── */}
      {contactModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: 'rgba(0,0,0,.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget && !contactSaving) setContactModal(false); }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center gap-2">
                  <Pencil size={16} /> Edit Contact Person
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setContactModal(false)}
                  disabled={contactSaving}
                />
              </div>
              <div className="modal-body">
                {contactError && (
                  <div className="alert alert-danger py-2 mb-3">{contactError}</div>
                )}
                <div className="mb-3">
                  <label className="form-label">First Name *</label>
                  <input
                    className="form-control"
                    value={contactForm.firstName}
                    onChange={e => setContactForm(f => ({ ...f, firstName: e.target.value }))}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Last Name *</label>
                  <input
                    className="form-control"
                    value={contactForm.lastName}
                    onChange={e => setContactForm(f => ({ ...f, lastName: e.target.value }))}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={contactForm.phone}
                    onChange={e => setContactForm(f => ({ ...f, phone: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    className="form-control"
                    value={contactForm.email}
                    onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))}
                    required
                  />
                </div>
                <div className="alert alert-warning py-2 mb-0 small">
                  ⚠️ This is also the customer's login email. If changed, they will need to use the new email address to sign in.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setContactModal(false)}
                  disabled={contactSaving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary d-inline-flex align-items-center gap-2"
                  onClick={handleSaveContact}
                  disabled={contactSaving}
                >
                  {contactSaving && <span className="spinner-border spinner-border-sm" />}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Impersonate confirmation modal ── */}
      {impersonateModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: 'rgba(0,0,0,.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget && !impersonateLoading) setImpersonateModal(false); }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center gap-2">
                  <Eye size={18} /> View as Customer
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setImpersonateModal(false)}
                  disabled={impersonateLoading}
                />
              </div>
              <div className="modal-body">
                <p>
                  This will open the customer portal as{' '}
                  <strong>{customer?.companyName}</strong>.
                  Your admin session will be restored when the preview ends.
                </p>
                <p className="text-muted small mb-0">
                  The preview token expires in 15 minutes. You will be automatically
                  returned to the admin panel when it expires.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setImpersonateModal(false)}
                  disabled={impersonateLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary d-inline-flex align-items-center gap-2"
                  onClick={handleImpersonate}
                  disabled={impersonateLoading}
                >
                  {impersonateLoading
                    ? <span className="spinner-border spinner-border-sm" />
                    : <Eye size={14} />}
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
